import { Banner, Button, LoadingSpinner } from '@neo4j-ndl/react';
import { PlusIconOutline } from '@neo4j-ndl/react/icons';
import { MODAL_TYPE, getApiError, getErrorMessage, useExternalConnectionsFlag, useModal } from '@nx/state';
import { EmptyState, SearchField } from '@nx/ui';
import cx from 'classnames';
import { useMemo } from 'react';

import { DeploymentList } from '../../deployments/deployments-list';
import { useDeployments } from '../../hooks/use-deployments';

export function DeploymentDataSource({
  filter,
  onFilterChange,
}: {
  filter: string;
  onFilterChange: (filter: string) => void;
}) {
  const connectionFormModal = useModal(MODAL_TYPE.CONNECTION_FORM);
  const externalConnectionsEnabled = useExternalConnectionsFlag();
  const { deployments, isDeploymentsLoading, error } = useDeployments({ shouldPoll: true });

  const errorMessage = useMemo(() => {
    if (error) {
      return getErrorMessage(getApiError(error));
    }

    return null;
  }, [error]);

  if (isDeploymentsLoading) {
    return <LoadingSpinner size="medium" className="relative left-[50%] top-[10%]" />;
  }

  if (errorMessage !== null) {
    return <Banner type="danger" description={errorMessage} className="m-8" usage="inline" />;
  }

  return (
    <div className="grid gap-6">
      <div className="flex">
        <SearchField
          className={cx('h-8 w-[320px]', { 'ml-auto': !externalConnectionsEnabled })}
          value={filter}
          onChange={(e) => onFilterChange(e.target.value)}
          htmlAttributes={{ 'aria-label': 'Search for deployments' }}
        />
        <div className="ml-auto">
          <Button
            fill="text"
            onClick={() =>
              connectionFormModal.open({
                error: undefined,
                hideConnectionMethods: undefined,
                initialValues: undefined,
                instanceDetails: undefined,
                source: { type: 'user' },
                warning: undefined,
              })
            }
          >
            <PlusIconOutline className="h-4 w-4" /> New connection
          </Button>
        </div>
      </div>
      {deployments.length > 0 ? (
        <DeploymentList deployments={deployments} searchFilter={filter} connectionCardDetails={true} />
      ) : (
        <EmptyState
          icon="database-add-circle"
          title="No deployments found"
          description="Create your first deployment to get started"
        />
      )}
    </div>
  );
}
